import { useEffect, useRef, useState } from 'react';
import { PhoneE164Schema, type UserRegion } from '@pomoshnik/shared';
import { PhoneInput } from '@/components/forms/PhoneInput';

/**
 * Мок-под-флоу «Изменить номер» для экрана 2.11.2 (Block 11 Phase 11.B, ADR-0091 §2).
 *
 * Телефон — якорь входа/идентичности (P-010): прямое текстовое сохранение
 * запрещено. Смена = OTP-под-флоу: новый номер → «Отправить код» → ввод кода →
 * «Подтвердить». **В 11.B — ЗАГЛУШКА** (нет реального контракта/эндпоинта; OTP-
 * инфра — задача 1.2 auth-бэка). Код в `profile.ts` НЕ входит — телефон вне
 * контракта `ProfileUpdateInput` (§1a). Успех обновляет только показ телефона
 * на экране (локальный state контейнера), без персиста.
 *
 * **5 состояний P-021** (idle/loading/success/error) на двух шагах:
 *   step 'phone': ввод номера → idle/loading(отправка)/error(невалидный номер).
 *   step 'code':  ввод кода → idle/loading(проверка)/success/error.
 * («empty» неприменимо — это форма, не список; паттерн RegisterScreen.)
 *
 * Используется `<dialog>` (встроенный focus-trap + Escape), как `VideoModal`.
 */

export type PhoneChangeStep = 'phone' | 'code';
export type PhoneChangeStatus = 'idle' | 'loading' | 'success' | 'error';

export interface PhoneChangeModalProps {
  region: UserRegion;
  /** Закрыть модалку без изменения номера. */
  onClose: () => void;
  /** Вызывается при подтверждении OTP (мок принимает любой код). */
  onConfirmed: (newPhoneE164: string) => void;
}

// Длительность мок-задержки «отправки кода» / «проверки» (loading-состояние).
export const OTP_STUB_MS = 600;

export function PhoneChangeModal({ region, onClose, onConfirmed }: PhoneChangeModalProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [step, setStep] = useState<PhoneChangeStep>('phone');
  const [status, setStatus] = useState<PhoneChangeStatus>('idle');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (!dialog.open) {
      try {
        dialog.showModal();
      } catch {
        dialog.setAttribute('open', '');
      }
    }
    const onCancel = (e: Event) => {
      e.preventDefault();
      onClose();
    };
    dialog.addEventListener('cancel', onCancel);
    return () => {
      dialog.removeEventListener('cancel', onCancel);
    };
  }, [onClose]);

  // Cleanup мок-таймера на unmount.
  useEffect(() => {
    return () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
    };
  }, []);

  const busy = status === 'loading';

  const handleSendCode = () => {
    if (busy) return;
    setErrorMsg(null);
    const valid = PhoneE164Schema.safeParse(phone).success;
    if (!valid) {
      setStatus('error');
      setErrorMsg('Введите корректный номер телефона.');
      return;
    }
    setStatus('loading');
    // МОК: «отправка кода». Реальный эндпоинт — задача 1.2.
    timerRef.current = setTimeout(() => {
      setStep('code');
      setStatus('idle');
      timerRef.current = null;
    }, OTP_STUB_MS);
  };

  const handleConfirm = () => {
    if (busy) return;
    setErrorMsg(null);
    if (code.trim().length === 0) {
      setStatus('error');
      setErrorMsg('Введите код из SMS.');
      return;
    }
    setStatus('loading');
    // МОК: «проверка кода» — принимает любой непустой код. Реальная проверка — 1.2.
    timerRef.current = setTimeout(() => {
      setStatus('success');
      timerRef.current = null;
      onConfirmed(phone);
    }, OTP_STUB_MS);
  };

  return (
    <dialog
      ref={ref}
      role="dialog"
      aria-modal="true"
      aria-label="Изменить номер телефона"
      className="m-auto max-w-[90vw] w-[420px] rounded-xl bg-card p-0 backdrop:bg-black/60"
    >
      <div className="relative bg-card p-6 flex flex-col gap-5">
        <button
          type="button"
          onClick={onClose}
          aria-label="Закрыть"
          className="absolute top-2 right-2 z-10 w-10 h-10 rounded-full bg-card border border-accent text-ink text-xl leading-none focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none"
        >
          ×
        </button>

        <h2 className="font-display text-h2 text-ink m-0">Изменить номер</h2>

        {step === 'phone' ? (
          <>
            <PhoneInput
              name="newPhone"
              label="Новый номер телефона"
              region={region}
              value={phone}
              onChange={setPhone}
              error={status === 'error' ? (errorMsg ?? undefined) : undefined}
              disabled={busy}
              required
            />
            <button
              type="button"
              onClick={handleSendCode}
              disabled={busy}
              aria-busy={busy ? true : undefined}
              className={
                'min-h-tap rounded-xl bg-cta text-ink text-base font-semibold px-4 py-3 ' +
                'border-b-2 border-cta-shadow ' +
                'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none ' +
                'disabled:opacity-60 disabled:cursor-not-allowed'
              }
            >
              {busy ? 'Отправляем код…' : 'Отправить код'}
            </button>
          </>
        ) : (
          <>
            <p className="text-muted text-[15px] m-0" aria-live="polite">
              Код отправлен на номер {phone}
            </p>
            <div className="flex flex-col">
              <label htmlFor="otp-code" className="text-ink font-semibold text-base mb-2">
                Код из SMS
              </label>
              <input
                id="otp-code"
                name="otpCode"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                disabled={busy || status === 'success'}
                aria-invalid={status === 'error' || undefined}
                className={
                  'min-h-tap rounded-xl bg-card text-ink text-base px-3 py-3 ' +
                  'border-[1.5px] ' +
                  (status === 'error' ? 'border-error ' : 'border-accent ') +
                  'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none ' +
                  'disabled:bg-icon-bg disabled:text-muted disabled:cursor-not-allowed'
                }
              />
              {status === 'error' && errorMsg ? (
                <p role="alert" className="text-error text-sm mt-1">
                  {errorMsg}
                </p>
              ) : null}
            </div>
            {status === 'success' ? (
              <p role="status" className="text-ink text-base font-medium m-0">
                Номер обновлён
              </p>
            ) : (
              <button
                type="button"
                onClick={handleConfirm}
                disabled={busy}
                aria-busy={busy ? true : undefined}
                className={
                  'min-h-tap rounded-xl bg-cta text-ink text-base font-semibold px-4 py-3 ' +
                  'border-b-2 border-cta-shadow ' +
                  'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none ' +
                  'disabled:opacity-60 disabled:cursor-not-allowed'
                }
              >
                {busy ? 'Проверяем…' : 'Подтвердить'}
              </button>
            )}
          </>
        )}
      </div>
    </dialog>
  );
}
